<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import UIKit, { type ITableColumn, type ITableSortSettings } from '@pocketprep/ui-kit'
import type { Teach } from '@pocketprep/types'
import { instructorModule } from '@/store/instructor/module'
import { examModule } from '@/store/exam/module'
import { groupModule } from '@/store/group/module'
import { organizationModule } from '@/store/organization/module'
import { toastModule } from '@/store/toast/module'

interface IAccountTypeOption {
    label: 'Admin' | 'Instructor'
    helperText: string
    value: 'admin' | 'instructor'
}

const {
    ModalContainer,
    SidePanel,
    Input: PocketInput,
    Select: PocketSelect,
    Button: PocketButton,
    Table,
} = UIKit

const props = defineProps<{ instructorId: string }>()
const emit = defineEmits<{ close: [value?: boolean] }>()

const showArchiveModal = ref(false)
const isLoading = ref(false)
const instructorSystemGroups = ref<Teach.Class.OrgGroupJSON[]>([])
const sortSettings = ref<ITableSortSettings | null>(null)

const accountTypeOptions: IAccountTypeOption[] = [
    {
        label: 'Admin',
        helperText:
        'Can view all student\'s stats and edit all students in the organization.<br>Can add students to any exam.',
        value: 'admin',
    },
    {
        label: 'Instructor',
        helperText: 'Can view their student\'s stats and edit their students.<br>Can add students to their exams.',
        value: 'instructor',
    },
]

const accountType = ref<IAccountTypeOption>({
    label: 'Instructor',
    helperText: 'placeholder',
    value: 'instructor',
})

const examColumns: ITableColumn[] = [{
    name: 'Exam Prep',
    propName: 'examPrep',
    styles: { width: '150px' },
}, {
    name: '# Students',
    propName: 'students',
    styles: { width: '100px', textAlign: 'center' },
}, {
    propName: 'remove',
    isLabelHidden: true,
    styles: { width: '100px' },
}]

const currentOrgInstructor = computed(() => instructorModule.getters.getCurrentOrgInstructor())
const isCurrentOrgInstructorAdmin = computed(() => !!currentOrgInstructor.value?.isAdmin)
const currentOrgInstructors = computed(() => instructorModule.getters.getCurrentOrgInstructors())
const currentOrgInstructorUsers = computed(() => instructorModule.getters.getCurrentOrgInstructorUsers())
const currentOrgExams = computed(() => examModule.getters.getCurrentOrgExams())
const examMetadata = computed(() => examModule.getters.getExamMetadata())
const guidOrgExamLib = computed(() => examModule.getters.getGuidOrgExamLib())
const currentVisibleOrgGroups = computed(() => groupModule.getters.getCurrentVisibleOrgGroups())

const studentExamLib = computed(() =>
    instructorSystemGroups.value.reduce((acc, group) => ({
        ...acc,
        [group.orgExam.objectId]: group.orgStudents.length,
    }), {} as { [key: string]: number })
)

const instructorExamIds = computed(() =>
    instructorSystemGroups.value.map(group => group.orgExam.objectId)
)

const mappedExams = computed(() => {
    const examIds = instructorExamIds.value
    const orgExams = currentOrgExams.value
    const meta = examMetadata.value
    const lib = studentExamLib.value
    return Object.values(
        examIds.reduce((acc, examId) => {
            const orgExam = orgExams[examId]
            const metaItem = orgExam && meta[orgExam.examMetadata.objectId]
            const examName = (orgExam?.displayName) || (metaItem?.nativeAppName)
            const examGuid = metaItem?.examGuid
            if (examGuid !== null && examGuid && !acc[examGuid]) {
                acc[examGuid] = {
                    id: examId,
                    examPrep: examName ?? '',
                    students: lib[examId] ?? 0,
                }
            }
            return acc
        }, {} as { [examGuid: string]: { id: string; examPrep: string; students: number } })
    )
})

const instructor = computed(() => currentOrgInstructors.value?.[props.instructorId])
const instructorUser = computed(() =>
    instructor.value && currentOrgInstructorUsers.value?.[instructor.value.user.objectId]
)

const closeArchiveModal = () => {
    showArchiveModal.value = false
}

const openArchiveModal = () => {
    showArchiveModal.value = true
}

const getGuidForOrgExamId = (orgExamId: string) =>
    examModule.getters.getExamGuidForOrgExamId(orgExamId)

const updateSortSettings = (newSortSettings: ITableSortSettings) => {
    sortSettings.value = newSortSettings
}

const removeExam = (examId: string) => {
    const examGuid = getGuidForOrgExamId(examId)
    if (!examGuid) {
        return
    }
    const orgExamIdsToRemove = guidOrgExamLib.value[examGuid]?.map(oe => oe.objectId)
    instructorSystemGroups.value = instructorSystemGroups.value.filter(
        g => !orgExamIdsToRemove?.includes(g.orgExam.objectId)
    )
}

const emitClose = () => {
    emit('close', true)
}

const archive = async () => {
    isLoading.value = true
    await instructorModule.actions.archiveInstructor({ instructorId: props.instructorId })
    toastModule.actions.displayToast({
        title: `${instructor.value?.isAdmin
            ? 'Admin'
            : 'Instructor'} ${instructorUser.value?.firstName} ${instructorUser.value?.lastName} archived.`,
    })
    isLoading.value = false
    emitClose()
}

const save = async () => {
    if (!currentOrgExams.value) {
        throw new Error('submitUpdateInstructor: No current exams found.')
    }
    if (!instructor.value) {
        throw new Error('submitUpdateInstructor: No instructor found.')
    }
    isLoading.value = true
    const orgExamIds = accountType.value.value === 'admin'
        ? Object.values(currentOrgExams.value).map(e => e?.objectId).filter((e): e is string => !!e)
        : instructorExamIds.value
    const examGuid = orgExamIds[0] && getGuidForOrgExamId(orgExamIds[0])

    await instructorModule.actions.updateInstructor({
        instructorId: props.instructorId,
        accountType: accountType.value.value,
        hasBillingAccess: false,
        orgExamIds,
    })

    const orgGroupIdsToHide = groupModule.getters.searchCurrentOrgGroups({
        instructorId: props.instructorId,
        examGuid,
    }).map(g => g.objectId)

    const currentVisibleOrgGroupIdsForInstructor = currentVisibleOrgGroups.value
        .filter(cvog => cvog.owner?.objectId === props.instructorId)
        .map(g => g.objectId)
    const visibleGroups = currentVisibleOrgGroupIdsForInstructor.filter(g => !orgGroupIdsToHide.includes(g))

    await instructorModule.actions.updateVisibleOrgGroups(visibleGroups)
    organizationModule.actions.validateOrgState()
    isLoading.value = false
    emitClose()
}

onMounted(async () => {
    accountType.value = accountTypeOptions[0] as IAccountTypeOption
    await Promise.all([
        instructorModule.actions.fetchCurrentOrgInstructors(),
        instructorModule.actions.fetchCurrentOrgInstructorUsers(),
        examModule.actions.fetchCurrentOrgExams(),
        examModule.actions.fetchExamMetadata(),
        groupModule.actions.fetchCurrentOrgGroups(),
    ])

    instructorSystemGroups.value = groupModule.getters.getCompositeGroups()
        .filter(g => g?.owner?.objectId === props.instructorId && g.type === 'system')
        .flatMap(entry => entry?.groups || [])

    const inst = instructor.value
    accountType.value = (inst?.isAdmin)
        ? accountTypeOptions[0] as IAccountTypeOption
        : accountTypeOptions[1] as IAccountTypeOption
})
</script>

<template>
    <Portal to="sidepanel">
        <Portal to="modal">
            <ModalContainer
                v-if="showArchiveModal"
                :show-close-button="true"
                :remove-head-border-bottom="true"
                @close="closeArchiveModal">
                <template #head>
                    <div
                        class="edit-instructor__archive-head"
                    >
                        Archive {{ accountType.value }}?
                    </div>
                </template>
                <div
                    class="edit-instructor__archive-modal"
                >
                    <span
                        class="edit-instructor__archive-text"
                    >
                        They will no longer have access to Instructor Dashboard.
                    </span>
                    <div
                        class="edit-instructor__archive-buttons"
                    >
                        <PocketButton
                            class="edit-instructor__never-mind-btn"
                            type="tertiary"
                            @click="showArchiveModal = false"
                        >
                            Never Mind
                        </PocketButton>
                        <PocketButton
                            class="edit-instructor__archive-btn"
                            type="primary-red"
                            @click="archive"
                        >
                            Archive {{ accountType.value }}
                        </PocketButton>
                    </div>
                </div>
            </ModalContainer>
        </Portal>
        <SidePanel
            :name="`Edit ${instructorUser ? `${instructorUser.firstName} ${instructorUser.lastName}` : 'Colleague'}`"
            :isTeachSidePanel="true"
            class="edit-instructor"
            @close="emitClose"
        >
            <PocketInput
                :modelValue="instructorUser && instructorUser.username"
                :disabled="true"
                label="Colleague's Email"
                class="edit-instructor__field"
            />
            <PocketSelect
                v-model="accountType"
                :disabled="!isCurrentOrgInstructorAdmin"
                :data="accountTypeOptions"
                label="Account Type"
                class="edit-instructor__select-field"
            />
            <template v-if="accountType.value !== 'admin'">
                <div class="edit-instructor__exam-label">
                    {{ instructorUser && instructorUser.firstName }} teaches {{ mappedExams.length }}
                    exam{{ instructorExamIds.length === 1 ? '' : 's' }}
                </div>
                <Table
                    :rows="mappedExams"
                    :columns="examColumns"
                    :show-header="false"
                    class="edit-instructor__exams"
                    @sort="updateSortSettings"
                >
                    <template #tableCellValue="{ row, column }">
                        <PocketButton
                            v-if="isCurrentOrgInstructorAdmin && column.propName === 'remove' && mappedExams.length > 1"
                            class="add-group__remove"
                            type="tertiary"
                            @click="removeExam(row.id)"
                        >
                            Remove
                        </PocketButton>
                    </template>
                </Table>
            </template>

            <template #action>
                <PocketButton
                    type="primary"
                    :disabled="isLoading || !isCurrentOrgInstructorAdmin"
                    @click="save"
                >
                    Save Changes
                </PocketButton>
            </template>
            <template #details>
                <PocketButton
                    type="tertiary-red-small"
                    :disabled="isLoading || !isCurrentOrgInstructorAdmin"
                    @click="openArchiveModal"
                >
                    Archive {{ instructorUser && instructorUser.firstName }}
                </PocketButton>
            </template>
        </SidePanel>
    </Portal>
</template>

<style lang="scss" scoped>
.edit-instructor {
    &__field {
        margin-left: -11px;
        margin-bottom: 27px;
    }

    &__select-field {
        margin-left: -11px;
        margin-bottom: 27px;
        max-width: 205px;
    }

    &__exam-label {
        margin-top: 41px;
        font-size: 14px;
        line-height: 19px;
        font-weight: 600;
        margin-bottom: 14px;
    }

    &__exams {
        margin-left: -11px;
    }

    &__archive-head {
        margin: 60px auto 25px;
        font-size: 18px;
        color: $brand-black;
        font-weight: 600;
    }

    &__archive-head,
    &__archive-btn {
        text-transform: capitalize;
    }

    &__archive-modal {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 374px;
        box-sizing: border-box;
    }

    &__never-mind-btn {
        margin-right: 18px;
        color: $slate;
    }

    &__archive-buttons {
        display: flex;
        margin: 32px 0 24px;
    }

    &__archive-text {
        font-size: 15px;
        color: $ash;
        width: 294px;
        text-align: center;
        margin: 20px 0 0;
    }
}
</style>
